import {
  DataTable,
  DataTableRowClickEvent,
  DataTableStateEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { OverlayPanel } from "primereact/overlaypanel";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { FilteredListDto, ListFilterDto } from "../../services/base/base.dto";
import { mergeRecursive } from "../../utils/helper";
import {
  Paginator,
  PaginatorNextPageLinkOptions,
  PaginatorPageChangeEvent,
  PaginatorPageLinksOptions,
  PaginatorPrevPageLinkOptions,
} from "primereact/paginator";
import { Ripple } from "primereact/ripple";
import classNames from "classnames";
import empty_folder from "../../assets/images/empty_folder.svg";

// updates the text input with a delay and improves performance.
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState<string>(value);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timeout);
    };
  }, [value]);

  return debouncedValue;
}

const ArfDataTable = (props: ArfDataTableProps) => {
  const { t } = useTranslation();
  const dt = useRef<DataTable<any>>(null);

  let defaults: ArfDataTableProps = {
    data: { data: [], totalCount: 0 },
    loading: false,
    columns: [],
    exportOptions: {
      enabled: true,
      filename: "Data",
      text: t("Export"),
    },
    actionOptions: {
      enabled: true,
      icon: "pi pi-plus",
      text: t("Action"),
    },
    paginatorOptions: {
      enabled: true,
      pageSize: 10,
    },
    filterOptions: {
      enabled: true,
      text: t("Search"),
      extendedFilterIcon: "pi pi-filter",
    },
    emptyMessageOptions: {
      buttonText: t("EmptyTableButtonText"),
      text: t("EmptyTableMessage"),
    },
  };

  props = mergeRecursive(defaults, props);

  const [sortField, setSortField] = useState<string>("id");
  const [filterText, setFilterText] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<any>(-1);
  const [page, setPage] = useState<any>(0);
  const op = useRef<OverlayPanel>(null);
  const filterTextDebouncedValue = useDebounce(filterText, 500);

  useEffect(() => {
    if (props.filterChange) {
      var orderBy = sortField;
      if (props.columns) {
        var columns = props.columns.filter((x) => x.field === sortField);
        if (columns && columns.length > 0 && columns[0].orderKey) {
          orderBy = columns[0].orderKey;
        }
      }

      let filter: ListFilterDto = {
        page: page + 1,
        pageSize: props.paginatorOptions?.pageSize,
        orderBy: orderBy,
        orderByDesc: sortOrder === -1,
        filter: filterTextDebouncedValue,
        filterKeys: props.columns
          .filter((x) => x.filterable)
          .map((y) => y.field),
      };

      props.filterChange(filter);
    }
  }, [sortField, sortOrder, filterTextDebouncedValue, page]);

  const searchPlaceHolder = () => {
    var str = props.filterOptions?.text;
    // var filterableColumnsHeaders = props.columns
    //   .filter((x) => x.filterable)
    //   .map((y) => y.header);
    // if (filterableColumnsHeaders && filterableColumnsHeaders.length > 0) {
    //   str = `${str} (${filterableColumnsHeaders.join(", ")})`;
    // }
    return str;
  };

  const header = () => {
    return (
      <div className="flex justify-content-between">
        {props.filterOptions?.enabled ? (
          <span className="p-input-icon-right w-4">
            <i className="pi pi-search" />
            <InputText
              className="w-full"
              type="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFilterText(e.target.value);
              }}
              placeholder={searchPlaceHolder()}
            />
          </span>
        ) : (
          <div></div>
        )}
        <div className="flex align-items-center justify-content-end gap-2">
          {props.exportOptions?.enabled ? (
            <Button
              type="button"
              className="button-white"
              icon="pi pi-download"
              onClick={exportExcel}
              label={props.exportOptions?.text}
              data-pr-tooltip="XLS"
            />
          ) : null}
          {props.filterOptions?.enableExtended ? (
            <>
              <Button
                type="button"
                className="button-white"
                icon={props.filterOptions.extendedFilterIcon}
                iconPos="right"
                onClick={(e) => op?.current?.toggle(e)}
                label={t("Filter")}
              />
              <OverlayPanel ref={op} className="extended-filter-overlay">
                {props.filterOptions.extendedTemplate}
              </OverlayPanel>
            </>
          ) : null}
          {props.actionOptions?.enabled ? (
            <>
              {props.actionOptions?.template ? (
                props.actionOptions?.template
              ) : (
                <Button
                  type="button"
                  icon={props.actionOptions?.icon}
                  label={props.actionOptions?.text}
                  onClick={() => {
                    if (props.actionOptions?.command) {
                      props.actionOptions?.command();
                    }
                  }}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    );
  };

  const footer = () => {
    return props.paginatorOptions?.enabled ? (
      <Paginator
        pageLinkSize={3}
        rows={props.paginatorOptions?.pageSize}
        first={page * (props.paginatorOptions?.pageSize as number)}
        template={{
          layout: "PrevPageLink PageLinks NextPageLink",
          PrevPageLink: (options: PaginatorPrevPageLinkOptions) => {
            return (
              <Button
                type="button"
                className="paginator-button prev"
                onClick={options.onClick}
                disabled={options.disabled}
              >
                <span className="py-1 px-2 text-sm font-medium">
                  {t("Previous")}
                </span>
                <Ripple />
              </Button>
            );
          },
          NextPageLink: (options: PaginatorNextPageLinkOptions) => {
            return (
              <Button
                type="button"
                className="paginator-button next"
                onClick={options.onClick}
                disabled={options.disabled}
              >
                <span className="py-1 px-2 text-sm font-medium">
                  {t("Next")}
                </span>
                <Ripple />
              </Button>
            );
          },
          PageLinks: (options: PaginatorPageLinksOptions) => {
            return (
              <Button
                type="button"
                className="paginator-button index"
                onClick={options.onClick}
              >
                <span
                  className={classNames("py-1 px-2 text-sm font-medium", {
                    "text-blue-500": page === options.page,
                  })}
                >
                  {options.page + 1}
                </span>
                <Ripple />
              </Button>
            );
          },
        }}
        totalRecords={props.data?.totalCount}
        onPageChange={(event: PaginatorPageChangeEvent) => {
          setPage(event.page);
        }}
        leftContent={
          <>
            {(props.data?.totalCount as number) >
            (props.paginatorOptions?.pageSize as number) ? (
              <span className="text-color">
                {t("GridShowingInfo", {
                  value1:
                    page * (props.paginatorOptions?.pageSize as number) + 1,
                  value2:
                    (page + 1) * (props.paginatorOptions?.pageSize as number),
                  totalCount: props.data?.totalCount,
                })}
              </span>
            ) : (
              <span className="text-color">
                {t("GridShowingInfoShort", {
                  totalCount: props.data?.totalCount,
                })}
              </span>
            )}
          </>
        }
      ></Paginator>
    ) : null;
  };

  const exportExcel = () => {
    dt?.current?.exportCSV();
    // const ws = XLSX.utils.json_to_sheet(props.data?.data as any[]);
    // const wb = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(wb, ws, "Data");
    // XLSX.writeFileXLSX(wb, props.exportOptions?.filename as string);
  };

  const emptyMessage = () => {
    const options = props.emptyMessageOptions;

    if (options?.template) {
      return options?.template;
    } else {
      return (
        <div className="flex flex-column gap-6 justify-content-center align-items-center m-5">
          <img src={empty_folder} alt="Empty Table" width="100px" />
          <span className="verify-text-color text-center">{options?.text}</span>
          {options?.enabled ? (
            <Button
              type="button"
              icon="pi pi-arrow-right"
              severity="success"
              label={options?.buttonText}
              iconPos="right"
              onClick={() => {
                if (props.actionOptions?.command) {
                  props.actionOptions?.command();
                }
              }}
            />
          ) : null}
        </div>
      );
    }
  };

  return (
    <>
      <DataTable
        ref={dt}
        value={props.data?.data}
        size={"small"}
        dataKey="id"
        header={header}
        footer={footer}
        emptyMessage={emptyMessage}
        scrollable
        scrollHeight={props.scrollHeight}
        showGridlines
        onSort={(event: DataTableStateEvent) => {
          setSortField(event.sortField);
          setSortOrder(event.sortOrder);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={props.loading}
        onRowClick={props.onRowClick}
        selectionMode={props.onRowClick ? "single" : undefined}
        exportFilename={props.exportOptions?.filename}
      >
        {props.columns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={t(col.header)}
            style={col.style}
            body={col.body}
            sortable={col.sortable}
          />
        ))}
      </DataTable>
    </>
  );
};

export interface ArfDataTableProps {
  data: FilteredListDto<any> | undefined;
  loading?: boolean | undefined;
  columns: ColumnMeta[];
  filterOptions?: FilterOptions;
  exportOptions?: ExportOptions;
  actionOptions?: ActionOptions;
  emptyMessageOptions?: EmptyMessageOptions;
  paginatorOptions?: PaginatorOptions;
  filterChange?: (filter: ListFilterDto) => void;
  onRowClick?: (event: DataTableRowClickEvent) => void;
  scrollHeight?: string;
}

export interface ColumnMeta {
  field: string;
  header: string;
  body?: React.ReactNode | ((data: any) => React.ReactNode);
  style?: CSSProperties;
  sortable?: boolean;
  filterable?: boolean;
  orderKey?: string;
}

export interface FilterOptions {
  enabled?: boolean;
  text?: string;
  enableExtended?: boolean;
  extendedTemplate?: React.ReactNode;
  extendedFilterIcon?: string;
}

export interface ExportOptions {
  enabled?: boolean;
  filename?: string;
  text?: string;
}

export interface PaginatorOptions {
  enabled?: boolean;
  pageSize?: number;
}

export interface ActionOptions {
  enabled?: boolean;
  text?: string;
  icon?: string;
  template?: React.ReactNode;
  command?: () => void;
}

export interface EmptyMessageOptions {
  text?: string;
  buttonText?: string;
  template?: React.ReactNode;
  enabled?: boolean;
}

export default ArfDataTable;
